<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { isRgbwwPm, real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Alert, Toggle, ToggleV2 } from '@Shelly/shelly-design-system';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../SettingGuide.svelte';

	export let device: Device;

	const deviceID = real_id(device.id);
	const isPro = isRgbwwPm(device.type);
	const component = isPro ? 'prorgbwwpm' : 'plusrgbwpm';
	const setConfigMethod = isPro ? 'ProRGBWWPM.SetConfig' : 'PlusRGBWpm.SetConfig';

	$: settingsStore = getDeviceSettings(deviceID);
	let settings = readDeviceSettings(deviceID);
	$: settings = $settingsStore ?? settings;

	let checked = settings?.[component]?.hf_mode;
	let cacheChecked = checked;
	let waitingForRequest = false;

	$: isDirty = checked !== cacheChecked;

	const sendRequests = async () => {
		if (waitingForRequest || !isDirty) return;
		waitingForRequest = true;
		const enable = checked;
		const res: SettingRequest = await gen2Request(device, setConfigMethod, {
			id: device.channel,
			config: { hf_mode: enable },
		});
		if (res.isok) {
			settings[component].hf_mode = checked = enable;
			devices_settings.update_devices_settings(deviceID, settings);
			cacheChecked = checked;
		} else {
			checked = cacheChecked;
		}
		onSettingsResponse(res, device);
		waitingForRequest = false;
	};

	const onToggle = () => {
		checked = !checked;
		if (!$showNewLayout) {
			sendRequests();
		}
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: sendRequests,
				loading: waitingForRequest,
				disabled: waitingForRequest || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	$: ToggleC = $showNewLayout ? ToggleV2 : Toggle;
</script>

{#if $showNewLayout}
	<SettingGuide
		text={$format('settings_guide_reduce_pwm_noise', {
			default:
				'Raise PWM frequency to cut driver buzz. Colors can look less accurate at low brightness.',
		})}
	/>
{/if}

<svelte:component this={ToggleC} {checked} size="m" on:click={onToggle} id="toggle-uxav7t3">
	{capFirstChar(
		$format('enable', {
			default: 'Enable',
		}),
	)}
</svelte:component>

{#if !$showNewLayout}
	<Alert
		class="mb-s"
		heading={$format('high_frequency_mode_desc', {
			default:
				'Increase PWM frequency to reduce the acoustic noise from the DC driver. This might cause non-precise colors in RGB/RGBW profiles when running at low brightness.',
		})}
	/>
{/if}
